import { useOrdenesSinUbicar, useUbicarPendientes } from './useUbicarOrdenes'
import estilos from './UbicarPendientes.module.css'

/**
 * UBICAR LAS ÓRDENES QUE NO TIENEN PUNTO.
 *
 * Esta maquinaria existía y funcionaba desde hacía meses. Vivía en UNA
 * pantalla —el mapa de calor por zona— y el mapa de órdenes, que es donde el
 * vacío se ve, solo sabía decir «23 sin ubicar» en gris y en letra chica, al
 * lado de la leyenda.
 *
 * Así que el Gerente abría el mapa, veía Cartago en blanco, y la única salida
 * estaba en otra pantalla a la que no tenía motivo de entrar. Una capacidad
 * que existe pero no se alcanza desde donde hace falta es, en la práctica, una
 * capacidad que no existe.
 *
 * Se saca aquí para poder ponerla en los dos sitios. Y el aviso de que las
 * direcciones van a Google se queda pegado al botón —no en una nota al pie—
 * porque es una decisión que toma quien lo pulsa: se manda la dirección y el
 * municipio, nada más, y cada consulta se cobra.
 */

type Props = {
  /**
   * `panel` cuando el mapa está VACÍO: es lo único que hay que hacer en esa
   * pantalla, y se pone en el medio. `linea` cuando ya hay puntos dibujados y
   * esto es un pendiente más, no el asunto principal.
   */
  variante?: 'panel' | 'linea'
}

export function UbicarPendientes({ variante = 'linea' }: Props) {
  const { pendientes } = useOrdenesSinUbicar()
  const { avance, ubicar } = useUbicarPendientes()

  // Sin nada pendiente no hay nada que decir. Un bloque que anuncia que todo
  // está bien es ruido permanente.
  if (pendientes.length === 0 && !avance.trabajando && !avance.error) return null

  const clase = [estilos.caja, variante === 'panel' ? estilos.panel : estilos.linea].join(' ')

  if (avance.trabajando) {
    return (
      <div className={clase} role="status">
        <p className={estilos.texto}>
          Buscando <b>{avance.actual}</b>…
        </p>
        {/* El avance es una barra y no solo un número: veintitrés consultas
            seguidas tardan, y sin nada que se mueva parece colgado. */}
        <div
          className={estilos.barra}
          role="progressbar"
          aria-valuenow={avance.hechas}
          aria-valuemin={0}
          aria-valuemax={avance.total}
        >
          <span style={{ width: `${(avance.hechas / Math.max(avance.total, 1)) * 100}%` }} />
        </div>
        <p className={estilos.pie}>
          {avance.hechas} de {avance.total} · {avance.encontradas} encontradas
        </p>
      </div>
    )
  }

  if (avance.error) {
    return (
      <div className={clase} role="alert">
        <p className={estilos.error}>{avance.error}</p>
        <p className={estilos.pie}>
          Se paró en seco a propósito: si Google está rechazando la llave, seguir pidiendo solo
          gasta consultas cobradas.
        </p>
        <button type="button" className={estilos.boton} onClick={() => void ubicar(pendientes)}>
          Reintentar
        </button>
      </div>
    )
  }

  return (
    <div className={clase}>
      <p className={estilos.texto}>
        <b>
          {pendientes.length} {pendientes.length === 1 ? 'orden' : 'órdenes'}
        </b>{' '}
        {pendientes.length === 1 ? 'no tiene' : 'no tienen'} punto en el mapa.
      </p>

      <button type="button" className={estilos.boton} onClick={() => void ubicar(pendientes)}>
        Ubicarlas en el mapa
      </button>

      <p className={estilos.pie}>
        Se manda a Google la dirección y el municipio —nada más: ni el nombre del cliente, ni el
        valor del trabajo—. Cada consulta se cobra.
      </p>
    </div>
  )
}
